import { Radio, RadioGroup, Tooltip } from "@blueprintjs/core"
import cc from "classcat"
import React from "react"
import { useState } from "react"

import styles from "./PlaceholderTransferConfirmationModal.module.css"

import DurationHelper from "~/helpers/DurationHelper"
import { MultiSelectAssignments } from "~/helpers/split-screen-helpers"

import { ModalBody, ModalFooter, ModalFormWrapper } from "~/common/ModalForm"
import Button from "~/common/buttons/Button"
import Checkbox from "~/common/inputs/Checkbox"
import { Help } from "~/common/react-icons"

import { usePermissions } from "~/Permissions/usePermissions"

const TRANSFER_MODE = {
  FULL: "full" as const,
  PARTIAL: "partial" as const,
}

const KeepPlaceholderCheckbox = ({
  label,
  id,
  checked,
  handleToggle,
  helpText,
  disabled,
}: {
  label: string
  id: string
  checked: boolean
  handleToggle: () => void
  disabled: boolean
  helpText?: string
}) => {
  return (
    <div className={styles.checkboxContainer}>
      <Checkbox
        id={id}
        label={label}
        onChange={handleToggle}
        checked={checked}
        disabled={disabled}
        labelClassName={cc([
          styles.checkboxLabel,
          { [styles.disabled]: disabled },
        ])}
        tickClassName={styles.checkboxTick}
      />
      {helpText && (
        <span className={styles.helpIcon}>
          <Tooltip content={helpText} placement="top">
            <Help />
          </Tooltip>
        </span>
      )}
    </div>
  )
}

type Props = {
  personName: string
  project: Pick<MultiSelectAssignments[number], "project_id" | "is_template">
  placeholderName: string
  isSaving: boolean
  handleSubmit: (
    deletePlaceholder: boolean,
    transferMode: "full" | "partial",
  ) => void
  closeDialog: () => void
  numberOfAssignments: number
  conflict?: {
    adjustedMinutes: number
    overlappingTimeOffsMinutes: number
    partiallyTransferableMinutes: number
  }
}

const PlaceholderTransferConfirmationModal = (props: Props) => {
  const {
    personName,
    project,
    placeholderName,
    numberOfAssignments,
    isSaving,
    closeDialog,
    handleSubmit,
    conflict,
  } = props

  const { can, subject } = usePermissions()
  const canDeletePlaceholder = can(
    "delete",
    subject("Placeholder", {
      project: { id: project.project_id, isTemplate: project.is_template },
    }),
  )

  const [mode, setMode] = useState<{
    transferMode: (typeof TRANSFER_MODE)[keyof typeof TRANSFER_MODE]
    keepPlaceholderChecked: boolean
  }>({
    transferMode: TRANSFER_MODE.FULL,
    keepPlaceholderChecked: !canDeletePlaceholder,
  })

  const handleToggleKeepPlaceholder = () => {
    setMode({
      ...mode,
      keepPlaceholderChecked: !mode.keepPlaceholderChecked,
    })
  }

  const onSubmit = () => {
    const deletePlaceholder = !mode.keepPlaceholderChecked
    const transferMode = mode.transferMode

    handleSubmit(deletePlaceholder, transferMode)
  }

  return (
    <ModalFormWrapper headerTitle="Confirm Transfer">
      <ModalBody>
        <div className={styles.wrapper}>
          {conflict ? (
            <b>
              {`${personName} has ${DurationHelper.formatDuration(conflict.overlappingTimeOffsMinutes)} of time off during the assignment being transferred.`}
            </b>
          ) : (
            <p>
              All assignments ({numberOfAssignments}) from{" "}
              <b>{placeholderName}</b> will be transferred to{" "}
              <b>{personName}</b>{" "}
            </p>
          )}
          {conflict ? (
            <RadioGroup
              label=""
              onChange={(value) => {
                setMode({
                  ...mode,
                  keepPlaceholderChecked:
                    value.currentTarget.value === TRANSFER_MODE.PARTIAL
                      ? true
                      : !canDeletePlaceholder,
                  transferMode: value.currentTarget
                    .value as (typeof TRANSFER_MODE)[keyof typeof TRANSFER_MODE],
                })
              }}
              selectedValue={mode.transferMode}
            >
              <Radio
                value={TRANSFER_MODE.FULL}
                labelElement={<b>Transfer full assignment anyway</b>}
              >
                {canDeletePlaceholder &&
                  mode.transferMode === TRANSFER_MODE.FULL && (
                    <KeepPlaceholderCheckbox
                      label="Keep empty placeholder"
                      id="full-mode-keep-placeholder"
                      checked={mode.keepPlaceholderChecked}
                      handleToggle={handleToggleKeepPlaceholder}
                      disabled={!canDeletePlaceholder}
                      helpText="Keep the empty placeholder to retain any information or notes associated with the placeholder"
                    />
                  )}
              </Radio>
              <Radio
                value={TRANSFER_MODE.PARTIAL}
                labelElement={<b>Transfer available portion of assignment</b>}
              >
                <p>
                  {`${DurationHelper.formatDuration(conflict.partiallyTransferableMinutes)} out of ${DurationHelper.formatDuration(conflict.adjustedMinutes)} will be transferred, ${DurationHelper.formatDuration(
                    conflict.adjustedMinutes -
                      conflict.partiallyTransferableMinutes,
                  )} will not be transferred.`}
                </p>
                {canDeletePlaceholder &&
                  mode.transferMode === TRANSFER_MODE.PARTIAL && (
                    <KeepPlaceholderCheckbox
                      label={`Keep placeholder with remaining ${DurationHelper.formatDuration(conflict.adjustedMinutes - conflict.partiallyTransferableMinutes)} assignment`}
                      id="partial-mode-keep-placeholder"
                      checked={mode.keepPlaceholderChecked}
                      handleToggle={handleToggleKeepPlaceholder}
                      disabled={!canDeletePlaceholder}
                    />
                  )}
              </Radio>
            </RadioGroup>
          ) : (
            <KeepPlaceholderCheckbox
              label="Keep empty placeholder"
              id="full-mode-keep-placeholder"
              checked={mode.keepPlaceholderChecked}
              handleToggle={handleToggleKeepPlaceholder}
              disabled={!canDeletePlaceholder}
              helpText="Keep the empty placeholder to retain any information or notes associated with the placeholder"
            />
          )}
        </div>
      </ModalBody>
      <ModalFooter>
        <Button disabled={isSaving} text="Cancel" onClick={closeDialog} />
        <Button
          text={"Save"}
          outlined={false}
          loading={isSaving}
          disabled={isSaving}
          onClick={onSubmit}
        />
      </ModalFooter>
    </ModalFormWrapper>
  )
}

export default PlaceholderTransferConfirmationModal
